<template>
  <div v-if="product" class="mt-4 lg:mt-6">
    <component
      :is="isOutOfStock ? 'div' : BaseSticky"
      name="productDetailsCta"
      :stick-above-element="stickAboveElement"
    >
      <div ref="productDetailsCtaRef" class="f-col gap-2">
        <vf-button
          v-if="(!isComingSoon && !isSignInToBuy && !isRobisOnly) && nextAttribute && !product.allVariantsOutOfStock"
          :class="{ '<lg:hidden': hideCtasOnMobile }"
          :size="ctaSize"
          @click="PanelVariantSelection.open({ attribute: nextAttribute })"
        >
          {{ $t.selectSize }}
        </vf-button>
        <product-notify-me
          v-else-if="isNotifyMe && ((selectedVariant && isSelectedProductOutOfStock)
            || isComingSoon || product.allVariantsOutOfStock)"
          :class="{ '<lg:hidden': hideCtasOnMobile }"
          :size="ctaSize"
        />
        <vf-button
          v-else-if="isComingSoon"
          :class="{ '<lg:hidden': hideCtasOnMobile }"
          disable-success-icon
          disabled
          :size="ctaSize"
        >
          {{ comingSoonText }}
        </vf-button>
        <template v-else-if="!isComingSoon">
          <vf-button
            v-if="!isProductOrInventoryPending && isOutOfStock && !isSignInToBuy"
            :class="{ '<lg:hidden': hideCtasOnMobile }"
            disable-success-icon
            disabled
            :size="ctaSize"
          >
            {{ $t.outOfStock }}
          </vf-button>
          <vf-button
            v-else-if="isRobisOnly"
            :class="{ '<lg:hidden': hideCtasOnMobile }"
            disable-success-icon
            :size="ctaSize"
            @click="handleReserveInStore"
          >
            {{ $t.reserveInStore }}
          </vf-button>
          <template v-else>
            <vf-button
              id="pdp-add-to-cart"
              aria-live="polite"
              :class="{ '<lg:hidden': hideCtasOnMobile }"
              disable-success-icon
              :disabled="!isProductOrInventoryPending && (addToCartLoading || checkoutCtaLoading)"
              :loading="isProductOrInventoryPending || (addToCartLoading && !checkoutCtaLoading)"
              :size="ctaSize"
              type="submit"
              @click="handleAddToCart"
            >
              {{ isSignInToBuy ? $t.signInToBuy : $t.addToCart }}
            </vf-button>
          </template>
        </template>
        <template v-if="!isComingSoon && !isRobisOnly">
          <client-only>
            <checkout-apple-pay-button v-if="isApplePayEnabled" class="<lg:mt-6" @click="handleApplePay" />
          </client-only>
          <base-button
            v-if="$feature.allowProductReservationOnPDP"
            class="py-2 underlined <md:px-4 <md:py-3 <md:b-b <md:b-t <md:b-grey-70 <md:bg-grey-90 <md:-mx-4"
            :size="ctaSize"
            :variant="ctaVariant"
            @click="handleReserveInStore"
          >
            <span class="text-center center i-flex">
              <vf-icon size="md" src="/icons/vans-bag.svg" />
              <span class="ml-2 underlined"> {{ $t.reserveInStore }} </span>
            </span>
          </base-button>
          <base-button
            v-else-if="$feature.showFindInStoreOnPDP"
            class="py-2 underlined <md:px-4 <md:py-3 <md:b-b <md:b-t <md:b-grey-70 <md:bg-grey-90 <md:-mx-4"
            :size="ctaSize"
            :variant="ctaVariant"
            @click="handleFindInStore"
          >
            <span class="underlined center i-flex">
              {{ $t.findInStore }}
            </span>
          </base-button>
        </template>
      </div>
    </component>

    <panel-variant-selection :size="{ sm: 'sm', lg: 'full' }" />
  </div>
</template>

<script lang="ts" setup>
import type { Props as VariantSelectionProps } from '#brand/components/dialog/variant-selection/VariantSelection.vue'
import { BaseSticky } from '#components'
import { formatDate } from '@vueuse/core'

const { hideCtasOnMobile = true } = defineProps<{ hideCtasOnMobile?: boolean }>()

const { details: { ctaSize, ctaVariant }, stickAboveElement } = useAppConfig().pages.pdp

const { $t, $feature } = useNuxtApp()

const auth = useAuthStore()
const cart = useCartStore()
const { DialogSignIn } = useDialogsStore()
const { allowApplePayOrderOnPDP, allowApplePayOrderOnPDPWithBopis, showTryItOnCtaOnPDP } = useFeatureFlags()
const {
  addToCart,
  addToCartLoading,
  attributeSelectionLabels,
  checkoutCtaLoading,
  findInStore,
  isProductOrInventoryPending,
  isComingSoon,
  isOutOfStock,
  isNotifyMe,
  isRobisOnly,
  isSelectedProductOutOfStock,
  isSignInToBuy,
  nextAttribute,
  product,
  selectedVariant,
  shippingMethod,
  triggerApplePaySession,
  tryItOn
} = usePdp()
const route = useRoute()
const productDetailsCtaRef = useState<HTMLElement>('productDetailsCtaRef')
const { pending: isCartPending } = storeToRefs(cart)

const PanelVariantSelection = createPanel('variant-selection')

const tryItOnProducts = ref()

const isApplePayEnabled = computed(() =>
  allowApplePayOrderOnPDP
  && window.ApplePaySession
  && (allowApplePayOrderOnPDPWithBopis || shippingMethod.value !== 'pickup')
)

const comingSoonText = computed(() => {
  if (product.value?.targetPublishDate) {
    const formattedDate = formatDate(
      new Date(product.value.targetPublishDate.replace(/-/g, '\/')),
      $feature.configProductComingSoonDateFormat
    )
    return `${$t.comingSoon} – ${formattedDate}`
  }

  return $t.comingSoon
})

const productHasTryItOnAsset = computed(() => product.value?.id
  && tryItOnProducts.value?.includes(product.value.id.toLowerCase()))

const handleNextAttribute = (options: Omit<VariantSelectionProps, 'attribute'> = {}) => {
  if (!nextAttribute.value) return

  PanelVariantSelection.open({ attribute: nextAttribute.value, ...options })
  throw new Error('Select Next Attribute')
}

const handleSignInToBuy = async () => {
  if (isSignInToBuy.value) {
    await DialogSignIn.open({
      formLocation: 'modal:single:pdp:none',
      isSignInToBuy: true
    })
    if (auth.loggedIn) {
      await until(isCartPending).toBe(false)
      return handleSignInToBuy()
    }
    throw new Error('User not signed in')
  }
}

const handleApplePay = async () => {
  try {
    await handleSignInToBuy()
    handleNextAttribute({ enableApplePay: true })
    triggerApplePaySession()
  }
  catch (e) {
    console.info(e)
  }
}

const handleFindInStore = () => {
  try {
    handleNextAttribute({ enableFindInStore: true })
    findInStore(attributeSelectionLabels.value, 'pickup')
  }
  catch (e) {
    console.info(e)
  }
}

const handleReserveInStore = () => {
  try {
    handleNextAttribute({ enableReserveInStore: true })
    findInStore(attributeSelectionLabels.value, 'reserve')
  }
  catch (e) {
    console.info(e)
  }
}

const handleAddToCart = async () => {
  try {
    await handleSignInToBuy()
    handleNextAttribute()
    await addToCart()
  }
  catch (e) {
    console.info(e)
  }
}

if (showTryItOnCtaOnPDP) {
  try {
    const { data } = await useApi().internal.getStaticPage('vyking-products')
    tryItOnProducts.value = data.value
  }
  catch { tryItOnProducts.value = [] }
}

if (route.path.includes('vyking') && productHasTryItOnAsset.value) tryItOn()
</script>
